import { buffers } from 'redux-saga';
import { actionChannel, call, put, select, take, takeEvery } from 'redux-saga/effects';
import { AsyncError, ErrorType } from '../../../common';
import { getServiceProvider, IServiceProvider } from '../../../services';
import { PayloadMetaAction, TypeConstant } from '../../../utils/reduxUtils';
import { showErrorDialog } from '../../common/utils/showDialog';
import { showErrorSnackbar } from '../../common/utils/showSnackbar';
import { requireUser, RequireUserResult } from '../../utils/sagas';
import actions from '../actions';
import { ICreateTourState } from '../models/state';
import { ICreateTourModel } from '../models/Tour';
import selectors from '../selectors';
import { mergeStateIntoTour } from '../utils/mergeStateIntoTour';

const triggerSave = takeEvery(
  [
    actions.updateTourField.type,
    actions.updateTourStopField.type,
    actions.deleteTourStop.type,
    actions.deleteImage.type,
    actions.reorderImages.type,
    actions.reorderTourStops.type,
    actions.updatePreviewTourStop.type,
  ],
  function* (action: PayloadMetaAction<TypeConstant, any, { save: boolean }>) {
    if (action.meta.save) {
      yield put(actions.saveTourAsync.request());
    }
  }
);

function* saveTour() {
  // discard duplicate actions in the buffer
  const requestChannel = yield actionChannel([actions.saveTourAsync.request.type], buffers.sliding(1));
  while (true) {
    yield take(requestChannel);
    yield call(updateAndSaveTour);
  }
}

function* updateAndSaveTour() {
  const serviceProvider: IServiceProvider = yield getServiceProvider();

  const currentState: ICreateTourState = yield select(selectors.selectCreateTourState);
  const tour: ICreateTourModel | undefined = currentState.tour;
  const result: RequireUserResult = yield requireUser();

  if (!result.user) {
    yield put(actions.saveTourAsync.failure(result.error));
  } else {
    const tourState = currentState.tourState;
    const updatedTour = mergeStateIntoTour(tourState, tour, result.user);

    try {
      const tourResponse: ICreateTourModel = yield updatedTour.id > 0
        ? serviceProvider.api.updateTour(updatedTour)
        : serviceProvider.api.createTour(updatedTour);
      yield put(actions.saveTourAsync.success(tourResponse));
    } catch (error) {
      yield put(actions.saveTourAsync.failure(error));
      shouldShowErrorDialog(error) ? yield showErrorDialog(error) : yield showErrorSnackbar(error);
      serviceProvider.analyticsService.logError(error);
    }
  }
}

function shouldShowErrorDialog(error: Error): boolean {
  return (
    error instanceof AsyncError &&
    [ErrorType.TOUR_VALIDATION_FAILED, ErrorType.TOUR_ALREADY_SOLD].indexOf(error.errorCode) >= 0
  );
}

const sagas = [call(saveTour), triggerSave];
export default sagas;
